import type { ReactNode } from "react";
import { Check, ChevronRight } from "lucide-react";

/**
 * What a new clinic still has to do before anyone can book it
 * (docs/ux/screens/BusinessHomeScreen.md).
 *
 * It replaces a banner that appeared only when the business had zero services
 * and said "add your first service to unlock your business tools". That was
 * true and unhelpful: it named one of the four things standing between a new
 * clinic and its first booking, and it framed the gate as a restriction rather
 * than as a next step. J2#2's tools gate is unchanged; this is the friendly
 * face of it.
 *
 * **Every step is completable and every step is checkable**, which is why the
 * list is what it is. The demo carried a fourth "complete your public profile"
 * step that was hardcoded incomplete, so it could never be ticked; a checklist
 * with a step nobody can finish is a nag, not a checklist.
 *
 * **The first three are what make a business bookable.** Without a service
 * there is nothing to book, without a practitioner there is nobody to book it
 * with, and without hours there is no time to book. The panel says so, because
 * a progress bar that mixes "customers cannot reach you" with "your logo is
 * missing" teaches people to ignore it.
 *
 * There is no Dismiss. The panel disappears on its own when the work is done,
 * and dismissing an unfinished one would need somewhere to remember the
 * dismissal, which is a column nobody should add for this.
 */

export interface SetupState {
  hasService: boolean;
  hasPractitioner: boolean;
  hasHours: boolean;
  hasLogo: boolean;
}

interface Step {
  label: string;
  hint: string;
  href: string;
  done: boolean;
  /** Does a customer hit a wall without it? */
  blocking: boolean;
}

function stepsFor(setup: SetupState): Step[] {
  return [
    {
      label: "Add your first service",
      hint: "What people can book, how long it takes, what it costs",
      href: "/business/services",
      done: setup.hasService,
      blocking: true,
    },
    {
      label: "Add your team",
      hint: "Who performs the services. A solo practice already has one",
      href: "/business/practitioners",
      done: setup.hasPractitioner,
      blocking: true,
    },
    {
      label: "Set when you work",
      // H40: the clinic's hours are the calendar for everybody here, so this
      // step is one grid rather than a round of per-person editing.
      hint: "The hours your clinic is open for appointments",
      href: "/business/hours",
      done: setup.hasHours,
      blocking: true,
    },
    {
      label: "Add your logo",
      hint: "Shown on your profile and in search results",
      href: "/business/profile",
      done: setup.hasLogo,
      blocking: false,
    },
  ];
}

/** How this host renders an in-app link (R22 T14). */
export type ChecklistLink = (props: {
  href: string;
  className?: string;
  children: ReactNode;
}) => ReactNode;

export function SetupChecklist({
  setup,
  Link,
}: {
  setup: SetupState;
  Link: ChecklistLink;
}) {
  const steps = stepsFor(setup);
  const done = steps.filter((s) => s.done).length;
  if (done === steps.length) return null;

  const bookable = steps.every((s) => !s.blocking || s.done);

  return (
    <div className="mb-4 rounded-xl border border-accent/40 bg-accent/10 p-5">
      <h2 className="font-bold text-ink">Finish setting up</h2>
      <p className="mt-0.5 text-sm text-body">
        <span className="font-mono font-semibold text-ink">
          {done} of {steps.length}
        </span>{" "}
        done.{" "}
        {bookable
          ? "Customers can book you already. The rest is polish."
          : "Customers cannot book you until the first three are done."}
      </p>

      <div className="mt-3 flex gap-1.5" aria-hidden>
        {steps.map((step) => (
          <span
            key={step.label}
            className={`h-1 flex-1 rounded-[2px] ${step.done ? "bg-accent" : "bg-line"}`}
          />
        ))}
      </div>

      <ul className="mt-4 space-y-1">
        {steps.map((step) =>
          step.done ? (
            <li key={step.label} className="flex items-center gap-3 px-2 py-2 text-sm">
              <Tick done />
              <span className="text-muted line-through">{step.label}</span>
            </li>
          ) : (
            <li key={step.label}>
              {/* The whole row is the link. A checklist whose items are not the
                  way to do the thing makes the reader hunt for the screen it
                  is talking about. */}
              <Link
                href={step.href}
                className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-surface"
              >
                <Tick done={false} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-ink">
                    {step.label}
                  </span>
                  <span className="block text-xs text-muted">{step.hint}</span>
                </span>
                <ChevronRight className="size-4 shrink-0 text-muted" aria-hidden />
              </Link>
            </li>
          ),
        )}
      </ul>
    </div>
  );
}

function Tick({ done }: { done: boolean }) {
  return (
    <span
      className={`flex size-5 shrink-0 items-center justify-center rounded-[4px] ${
        done ? "bg-primary text-on-primary" : "border border-line bg-surface"
      }`}
      aria-hidden
    >
      {done && <Check className="size-3.5" strokeWidth={2.5} />}
    </span>
  );
}
